// src/pages/ForfaitPresale.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaTicketAlt, FaStar, FaCrown, FaCheckCircle, FaClock, FaLock, FaUnlockAlt, FaInfoCircle, FaShieldAlt, FaSync } from 'react-icons/fa';
import { supabase } from '../services/supabaseClient';
import { useCart } from '../context/CartContext';

// --- TYPES ---
type PresaleStep = 'LOCKED' | 'WAITING' | 'UNLOCKED';

// --- COMPOSANT : CHRONOMÈTRE DE LA BOUTIQUE (15 MIN) ---
const CheckoutTimer = ({ onExpire }: { onExpire: () => void }) => {
  const [timeLeft, setTimeLeft] = useState(15 * 60);

  useEffect(() => {
    if (timeLeft <= 0) {
      onExpire();
      return;
    }
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, onExpire]);

  return (
    <div className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full font-mono font-bold text-sm border border-red-200 shadow-sm">
      <FaClock className="animate-pulse" />
      Temps restant : {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}
    </div>
  );
};

// --- COMPOSANT : TEMPS PASSÉ DANS LA FILE D'ATTENTE ---
const ElapsedTimer = ({ startTime }: { startTime: Date }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;

  return (
    <span className="font-mono font-bold">
      {h > 0 && `${h} h `}{m} min {String(s).padStart(2, '0')} s
    </span>
  );
};

export default function ForfaitPresale() {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [step, setStep] = useState<PresaleStep>('LOCKED');
  
  // États Mot de passe
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // États File d'attente
  const [queueId, setQueueId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [joinedAt, setJoinedAt] = useState<Date | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  // États pour les vraies offres de la base de données
  const [presaleOffers, setPresaleOffers] = useState<any[]>([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);

  // --- ÉTAPE 1 : VALIDATION DU MOT DE PASSE VIA SUPABASE ---
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    setErrorMsg('');

    try {
      const safeCode = password.toLowerCase().trim();
      const { data: newQueueId, error } = await supabase.rpc('join_presale_queue', { 
        p_secret_code: safeCode 
      });

      if (error) throw error;
      if (!newQueueId) throw new Error("Impossible de générer l'ID de session.");

      setQueueId(newQueueId);
      setJoinedAt(new Date());
      setStep('WAITING');
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || "Code invalide ou expiré.");
    } finally {
      setIsVerifying(false);
    }
  };

  // --- ÉTAPE 2 : ÉCOUTE EN TEMPS RÉEL DE LA FILE D'ATTENTE ---
  useEffect(() => {
    if (step !== 'WAITING' || !queueId) return;

    const fetchCurrentStatus = async () => {
      const { data } = await supabase
        .from('presale_queue')
        .select('progress_percentage, status')
        .eq('id', queueId)
        .single();
      
      if (data) {
        setProgress(data.progress_percentage);
        if (data.status === 'active') setStep('UNLOCKED');
        if (data.status === 'expired') handleTimeExpired(false);
      }
    };
    fetchCurrentStatus();

    const channel = supabase.channel(`queue_${queueId}`)
      .on(
        'postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'presale_queue', filter: `id=eq.${queueId}` }, 
        (payload) => {
          setLastUpdated(new Date());
          setProgress(payload.new.progress_percentage);
          
          if (payload.new.status === 'active') {
            setStep('UNLOCKED');
          } else if (payload.new.status === 'expired') {
            handleTimeExpired(false);
          }
        }
      ).subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [step, queueId]);

  // --- ÉTAPE 3 : CHARGEMENT DES VRAIES OFFRES ---
  useEffect(() => {
    if (step === 'UNLOCKED') {
      const fetchOffers = async () => {
        setIsLoadingOffers(true);
        try {
          const safeCode = password.toLowerCase().trim();
          const { data, error } = await supabase
            .from('presale_offers')
            .select('*')
            .eq('secret_code', safeCode)
            .gt('quantity_available', 0)
            .order('price', { ascending: true });

          if (error) throw error;
          setPresaleOffers(data || []);
        } catch (err) {
          console.error("Erreur chargement offres:", err);
        } finally {
          setIsLoadingOffers(false);
        }
      };

      fetchOffers();
    }
  }, [step, password]);

  // --- ÉTAPE 4 : GESTION DE L'EXPIRATION DU TEMPS ---
  const handleTimeExpired = async (triggerBackend = true) => {
    alert("Votre temps alloué est écoulé. Votre session a expiré pour laisser la place aux suivants.");
    
    if (triggerBackend && queueId) {
      await supabase.rpc('expire_queue_session', { p_queue_id: queueId });
    }
    
    setStep('LOCKED');
    setPassword('');
    setProgress(0);
    setQueueId(null);
    setJoinedAt(null);
  };

  // --- ÉTAPE 5 : VALIDATION DE L'ACHAT ET AJOUT AU PANIER ---
  const handlePurchase = async (forfaitId: string) => {
    setIsPurchasing(true);
    try {
      const { data: isValid, error } = await supabase.rpc('validate_presale_purchase', { 
        p_queue_id: queueId, 
        p_forfait_id: forfaitId 
      });

      if (error) throw error;
      
      if (isValid) {
        const selectedOffer = presaleOffers.find(o => o.id === forfaitId);
        if (!selectedOffer) throw new Error("Article introuvable.");

        // ✅ CORRECTION ICI : Type 'product' pour activer la livraison, et bonne image.
        addToCart({
          id: selectedOffer.id,
          source_table: 'presale_offers',
          source_id: selectedOffer.id,
          type: 'product', // 🚚 Permet d'activer Shippo dans Checkout.tsx
          title: `Prévente VIP - ${selectedOffer.title}`,
          price: selectedOffer.price,
          original_price: selectedOffer.original_price,
          quantity: 1,
          image: selectedOffer.image_url, 
          image_url: selectedOffer.image_url, 
          business_id: selectedOffer.business_id 
        });

        navigate('/checkout');
      }
    } catch (err: any) {
      alert(err.message || "Erreur lors de la validation. La session a peut-être expiré.");
      handleTimeExpired(false);
    } finally {
      setIsPurchasing(false);
    }
  };


  // ==========================================
  // RENDER DES VUES
  // ==========================================

  // ÉCRAN 1 : LE PORTAIL VIP
  if (step === 'LOCKED') {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex flex-col font-sans relative">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=2000&auto=format&fit=crop" 
            alt="VIP Experience" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/80 to-transparent" />
        </div>

        <button onClick={() => navigate('/')} className="absolute top-6 left-6 z-20 text-white/50 hover:text-white text-sm font-bold uppercase tracking-widest transition">
          ← Retour à l'accueil
        </button>

        <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center justify-center max-w-6xl mx-auto w-full px-4 gap-12 py-20">
          <div className="flex-1 text-white max-w-lg">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 border border-blue-500/30 backdrop-blur-sm">
              <FaStar /> Événement Privé
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
              Portail de Prévente <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Exclusive</span>
            </h1>
            <p className="text-slate-300 mb-8 leading-relaxed text-lg">
              Bienvenue dans l'espace sécurisé Forfeo. Nos partenaires locaux libèrent un nombre très limité d'articles à des tarifs confidentiels.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="mt-1 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-blue-400"><FaLock size={12}/></div>
                <div>
                  <h3 className="font-bold text-white mb-1">Accès sur invitation</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">Seuls les membres possédant un code valide peuvent entrer dans la file d'attente sécurisée.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-blue-400"><FaClock size={12}/></div>
                <div>
                  <h3 className="font-bold text-white mb-1">File d'attente équitable</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">Premier arrivé, premier servi. À votre tour, vous disposerez de 15 minutes pour finaliser votre réservation.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-md bg-white/5 backdrop-blur-2xl p-8 rounded-3xl border border-white/10 shadow-2xl relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-500 rounded-full blur-[80px] opacity-30"></div>
            
            <div className="text-center mb-8 relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30 text-white text-2xl">
                <FaUnlockAlt />
              </div>
              <h2 className="text-2xl font-black text-white">Entrez votre code</h2>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4 relative z-10">
              <div>
                <input 
                  type="text" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="CODE D'ACCÈS" 
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-4 text-center text-white font-mono tracking-widest text-lg outline-none focus:border-blue-500 focus:bg-black/80 transition-all placeholder:text-slate-600 uppercase"
                  required
                />
              </div>
              
              {errorMsg && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold text-center py-3 rounded-xl flex items-center justify-center gap-2">
                  <FaInfoCircle /> {errorMsg}
                </div>
              )}
              
              <button 
                type="submit" 
                disabled={isVerifying || !password}
                className="w-full bg-white text-slate-900 hover:bg-blue-50 disabled:opacity-50 font-black py-4 rounded-xl transition shadow-lg mt-2 uppercase tracking-widest text-sm"
              >
                {isVerifying ? 'Vérification...' : "Rejoindre la file d'attente"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ÉCRAN 2 : LA FILE D'ATTENTE
  if (step === 'WAITING') {
    return (
      <div className="min-h-screen bg-[#e8f0fe] flex flex-col items-center justify-center p-4 font-sans">
        <div className="mb-10 text-center">
          <p className="text-xs font-black tracking-widest uppercase text-slate-500 mb-2">Forfeo Explore présente</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-slate-900">
            PRÉVENTE EXCLUSIVE
          </h1>
        </div>

        <div className="bg-white rounded-[2rem] p-8 md:p-12 w-full max-w-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] relative overflow-hidden text-slate-900">
          <p className="text-center text-lg md:text-xl font-medium leading-relaxed mb-10">
            Vous êtes en file pour la mise en vente. À votre tour, vous aurez <span className="font-bold">15 minutes</span> pour faire votre sélection et compléter votre achat dans la boutique.
          </p>

          <div className="h-6 w-full rounded-full border border-slate-300 p-0.5 mb-8 bg-white overflow-hidden">
            <div 
              className="bg-slate-900 h-full rounded-full transition-all duration-1000 ease-out relative" 
              style={{ width: `${Math.max(2, progress)}%` }}
            >
              <div className="absolute top-0 left-0 bottom-0 w-20 bg-white/10 skew-x-[45deg] animate-[shimmer_2s_infinite]"></div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-2 mb-10 text-sm">
            <p className="text-slate-600">
              Temps d'attente écoulé : {joinedAt ? <ElapsedTimer startTime={joinedAt} /> : '...'}
            </p>
            <p className="text-slate-400 text-xs">
              Dernière mise à jour : {lastUpdated.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          </div>

          <div className="border-t border-slate-100 pt-8 text-center flex flex-col items-center">
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2">ID de file d'attente sécurisée</p>
            <p className="text-xs text-slate-500 font-mono break-all">{queueId}</p>
            <div className="mt-6 flex items-center gap-2 text-slate-300 font-black text-xl tracking-tighter opacity-50">
               <FaShieldAlt /> FORFEO<span className="text-slate-800">SECURE</span>
            </div>
          </div>
        </div>
        <style dangerouslySetInnerHTML={{__html: `@keyframes shimmer { 0% { transform: translateX(-100%) skewX(45deg); } 100% { transform: translateX(500%) skewX(45deg); } }`}} />
      </div>
    );
  }

  // ÉCRAN 3 : LA BOUTIQUE (UNLOCKED)
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />
      
      <div className="pt-28 pb-12 px-4 max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-500">
        
        <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-3xl shadow-sm border border-slate-200 mb-10 gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xl shrink-0">
              <FaUnlockAlt />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900">C'est votre tour ! 🎉</h1>
              {/* ✅ CORRECTION ICI : Changement du texte */}
              <p className="text-slate-500 text-sm">Votre session est active. Choisissez rapidement votre article.</p>
            </div>
          </div>
          <CheckoutTimer onExpire={() => handleTimeExpired(true)} />
        </div>

        {/* Grille Dynamique des vraies offres */}
        {isLoadingOffers ? (
          <div className="flex flex-col items-center justify-center py-20 text-blue-600">
            <FaSync className="animate-spin text-4xl mb-4" />
            <p className="font-bold">Chargement des offres VIP...</p>
          </div>
        ) : presaleOffers.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-slate-200 shadow-sm">
            <p className="text-slate-500 font-bold text-lg">Oups ! Aucune offre n'est disponible pour le moment.</p>
            <p className="text-slate-400 mt-2">Le stock est peut-être épuisé.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {presaleOffers.map((offer: any) => (
              <div key={offer.id} className="relative bg-white rounded-3xl p-8 border-2 flex flex-col h-full transition-transform hover:-translate-y-1 border-slate-100 shadow-sm hover:shadow-xl">
                
                {offer.quantity_available < 10 && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-md whitespace-nowrap animate-pulse">
                    Presque épuisé ! ({offer.quantity_available} restants)
                  </div>
                )}
                
                <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl mb-6 shadow-sm border border-blue-100 overflow-hidden">
                  {offer.image_url ? (
                    <img src={offer.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <FaTicketAlt />
                  )}
                </div>
                
                <h3 className="text-2xl font-black text-slate-900 mb-2">{offer.title}</h3>
                <p className="text-slate-500 text-sm mb-6 flex-1 leading-relaxed">{offer.description}</p>
                
                <div className="mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-100 flex justify-between items-end">
                  <div>
                    {offer.original_price && (
                      <p className="text-xs text-slate-400 line-through font-bold">{offer.original_price}$</p>
                    )}
                    <span className="text-4xl font-black text-slate-900">{offer.price}$</span>
                  </div>
                  <span className="text-slate-500 text-sm font-medium"> / VIP</span>
                </div>
                
                {/* Liste des caractéristiques si elles existent */}
                {offer.features && offer.features.length > 0 && (
                  <ul className="space-y-4 mb-8">
                    {offer.features.map((feat: string, i: number) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700 font-medium">
                        <FaCheckCircle className="text-green-500 mt-0.5 shrink-0" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                )}
                
                <button 
                  onClick={() => handlePurchase(offer.id)}
                  disabled={isPurchasing}
                  className="mt-auto w-full py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all bg-slate-900 text-white hover:bg-blue-600 shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isPurchasing ? <><FaSync className="animate-spin" /> Validation...</> : 'Ajouter au panier'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
